import { getLeadFormQueryOptions } from '@/features/lead-forms/api/get-lead-form/get-lead-form.hook';
import { getAdQueryOptions } from '@/features/meta-ads/api/get-ad/get-ad.hook';
import { listCampaignsQueryOptions } from '@/features/meta-campaigns/api/list-campaigns/list-campaigns.hook';
import type { Campaign } from '@/features/meta-campaigns/api/types';
import type { CampaignTargeting } from '@/features/meta-campaigns/api/types';
import { useQuery } from '@tanstack/react-query';
import type { AdPreviewProps } from './ad-preview-card';

/** One shared campaign cache; refetch only while a saved card is mounted/visible. */
export function useSavedCampaign(campaignId?: string | null) {
  const query = useQuery({
    ...listCampaignsQueryOptions(),
    enabled: !!campaignId,
    refetchInterval: 60_000,
    refetchOnWindowFocus: 'always',
  });
  return query.data?.campaigns.find((campaign) => campaign.id === campaignId);
}

export function savedCampaignBudget(campaign: Campaign): string | undefined {
  const lifetime = Number(campaign.lifetimeBudget);
  const amount = lifetime > 0 ? lifetime : Number(campaign.dailyBudget);
  if (!Number.isFinite(amount) || amount <= 0 || !campaign.currency) return;
  const money = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: campaign.currency,
  }).format(amount / 100);
  return `${money}${lifetime > 0 ? ' lifetime' : '/day'}`;
}

export function savedCampaignTargeting(
  campaign: Campaign,
  override?: CampaignTargeting | null
): string | undefined {
  const targeting = { ...campaign.targeting, ...override };
  const place = targeting.location;
  const area =
    Array.isArray(targeting.countries) && targeting.countries.length
      ? `Countries: ${targeting.countries.join(', ')}`
      : typeof place === 'string'
        ? typeof targeting.distanceKm === 'number'
          ? `Within ${targeting.distanceKm} km of ${place}`
          : place
        : undefined;
  const audience = [
    typeof targeting.ageMin === 'number' && typeof targeting.ageMax === 'number'
      ? `Ages ${targeting.ageMin}–${targeting.ageMax}`
      : undefined,
    Array.isArray(targeting.genders) && targeting.genders.length === 1
      ? targeting.genders[0] === 1
        ? 'Men'
        : 'Women'
      : undefined,
  ]
    .filter(Boolean)
    .join('; ');
  return [area, audience].filter(Boolean).join('; ') || undefined;
}

/** Historical tool outputs are a fallback; edits always display the saved entity. */
export function useSavedAdPreview(snapshot: AdPreviewProps): AdPreviewProps {
  const query = useQuery({
    ...getAdQueryOptions(snapshot.adId ?? ''),
    refetchInterval: 15_000,
    refetchOnWindowFocus: 'always',
  });
  const ad = query.data?.id === snapshot.adId ? query.data : undefined;
  const campaign = useSavedCampaign(ad?.metaCampaignId);
  const form = useQuery({
    ...getLeadFormQueryOptions(ad?.leadFormId ?? ''),
    enabled: !!ad?.leadFormId,
    refetchInterval: 15_000,
  }).data;
  if (!ad)
    return snapshot.adId
      ? {
          ...snapshot,
          savedAdStatus: query.isError
            ? 'unavailable'
            : query.isPending
              ? 'loading'
              : snapshot.savedAdStatus,
        }
      : snapshot;

  const media = ad.graphicImageUrl
    ? {
        assetImageUrl: ad.graphicImageUrl,
        assetVideoUrl: undefined,
        assetThumbnailUrl: undefined,
      }
    : ad.video
      ? ad.video.mediaType === 'image'
        ? {
            assetImageUrl:
              ad.video.videoUrl ?? ad.video.thumbnailUrl ?? undefined,
            assetVideoUrl: undefined,
            assetThumbnailUrl: undefined,
          }
        : {
            assetImageUrl: undefined,
            assetVideoUrl: ad.video.videoUrl ?? undefined,
            assetThumbnailUrl: ad.video.thumbnailUrl ?? undefined,
          }
      : ad.videoId === snapshot.videoId && ad.graphicId === snapshot.graphicId
        ? {}
        : {
            assetImageUrl: undefined,
            assetVideoUrl: undefined,
            assetThumbnailUrl: undefined,
          };
  const budget = campaign ? savedCampaignBudget(campaign) : undefined;
  const targeting = campaign ? savedCampaignTargeting(campaign) : undefined;
  return {
    ...snapshot,
    ...media,
    adName: ad.name ?? snapshot.adName,
    headline:
      ad.headline === undefined
        ? snapshot.headline
        : (ad.headline ?? undefined),
    primaryText:
      ad.primaryText === undefined
        ? snapshot.primaryText
        : (ad.primaryText ?? undefined),
    description:
      ad.description === undefined
        ? snapshot.description
        : (ad.description ?? undefined),
    callToAction: ad.callToAction ?? snapshot.callToAction,
    destinationUrl:
      ad.destinationUrl === undefined
        ? snapshot.destinationUrl
        : (ad.destinationUrl ?? undefined),
    videoId:
      ad.videoId === undefined ? snapshot.videoId : (ad.videoId ?? undefined),
    graphicId:
      ad.graphicId === undefined
        ? snapshot.graphicId
        : (ad.graphicId ?? undefined),
    savedAdStatus: ad.status,
    campaignName: campaign?.name ?? snapshot.campaignName,
    targetingDisplay: targeting ?? snapshot.targetingDisplay,
    details: snapshot.details?.map((detail) => ({
      ...detail,
      title:
        detail.title === snapshot.campaignName && campaign
          ? campaign.name
          : detail.title,
      fields: detail.fields.map((field) => {
        if (field.label === 'Name' && campaign)
          return { ...field, value: campaign.name };
        if (field.label === 'Daily budget' && budget)
          return { ...field, value: budget };
        if ((field.label === 'Area' || field.label === 'Audience') && targeting)
          return { ...field, value: targeting };
        return field;
      }),
    })),
    budgetDisplay: budget ?? snapshot.budgetDisplay,
    promotion: ad.graphicOffer
      ? { id: ad.graphicOffer.id, name: ad.graphicOffer.name }
      : ad.graphicOffer === null &&
          (ad.graphicId != null || snapshot.graphicId != null)
        ? undefined
        : snapshot.promotion,
    leadForm:
      ad.leadFormId === undefined
        ? snapshot.leadForm
        : form
          ? { id: form.id, name: form.name }
          : ad.leadFormId
            ? snapshot.leadForm
            : undefined,
  };
}
